import SettlementStatementModal from "@/components/Modals/SettlementStatementModal.tsx";
import { useDisbursementsCalculations } from "@/hooks/useDisbursementsCalculations";
import { useDeleteDisbursementRowMutation } from "@/mutations/useDeleteDisbursementRowMutation.ts";
import { useSaveAttorneyFeeSettings } from "@/mutations/useSaveAttorneyFeeSettings.tsx";
import { useSaveCostRowMutation } from "@/mutations/useSaveCostRowMutation.tsx";
import { useSaveDisbursementRowMutation } from "@/mutations/useSaveDisbursementRowMutation.ts";
import { useSaveNegotiatedAmounts } from "@/mutations/useSaveNegotiatedAmounts.tsx";
import { useFetchCaseByUUID } from "@/queries/useFetchCaseByUUID.ts";
import {
    type DisbursementRow,
    useFetchDisbursementRows,
} from "@/queries/useFetchDisbursementRows.ts";
import { formatCurrency, parseNumber } from "@/utils/currencyUtils";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Check, Download, CreditCard as Edit, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// Styled Components
const DisbursementsTable = styled(TableContainer)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    "& .MuiTable-root": {
        borderCollapse: "separate",
        borderSpacing: 0,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontSize: "0.875rem",
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
        borderRight: "none",
    },
    position: "relative",
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
}));

const CompanyCell = styled(StyledTableCell)(() => ({
    minWidth: 200,
    maxWidth: 250,
}));

const EditableCell = styled(StyledTableCell)(() => ({
    "&:hover .edit-button": {
        opacity: 1,
    },
}));

const CellContent = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minHeight: 40,
});

const EditButton = styled(IconButton)(({ theme }) => ({
    opacity: 0.3,
    transition: "opacity 0.2s, background-color 0.2s",
    padding: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    "&:hover": {
        opacity: 1,
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
    },
}));

const EditInput = styled(TextField)(({ theme }) => ({
    "& .MuiInputBase-root": {
        fontSize: "0.875rem",
        padding: theme.spacing(0.75, 1),
        minHeight: 32,
    },
    "& .MuiInputBase-input": {
        padding: theme.spacing(0.75, 1),
        textAlign: "center",
    },
}));

const EditActions = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
}));

const EditableRow = styled(TableRow)(() => ({
    "&:hover": {
        "& .edit-button": {
            opacity: 0.7,
        },
    },
}));

const SpecialRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== "rowType",
})<{ rowType: "cost" | "atty" | "client" | "total" }>(({ theme, rowType }) => {
    // Colors per CAM-364 ticket requirements
    let backgroundColor = "transparent";
    if (rowType === "cost") {
        backgroundColor = "#fff3cd"; // Yellow background for COST
    } else if (rowType === "atty") {
        backgroundColor = "#cfe2ff"; // Blue background for Attorney Fee
    } else if (rowType === "client") {
        backgroundColor = "#d1e7dd"; // Green background for Client Distribution
    } else if (rowType === "total") {
        backgroundColor = theme.palette.grey[200]; // Gray background for Settlement Total
    }

    return {
        backgroundColor,
        "& td": {
            fontWeight: 600,
        },
    };
});

const AutoPopulatedChip = styled(Chip)(({ theme }) => ({
    fontSize: "0.65rem",
    height: 20,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
    alignSelf: "flex-start",
    "& .MuiChip-label": {
        padding: theme.spacing(0, 1),
    },
}));

const CompanyName = styled(Typography)(() => ({
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.2,
}));

const AccountInfo = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
}));

interface EditingCell {
    rowId: string;
    field: string;
}

interface CostData {
    bill: string;
    insurance: string;
    adj: string;
    self: string;
    medPay: string;
    currentBalance: string;
    reduction: string;
    sent: string;
    approved: string;
}

type Props = {
    caseIdUUID: string;
};

const MrtDisbursementsTable = ({ caseIdUUID }: Props) => {
    const { data, isError, isLoading } = useFetchDisbursementRows({
        caseId: caseIdUUID,
    });

    const { data: caseData } = useFetchCaseByUUID({
        isQueryEnabled: !!caseIdUUID,
        caseUUID: caseIdUUID,
    });

    const saveMutation = useSaveDisbursementRowMutation();
    const deleteMutation = useDeleteDisbursementRowMutation();
    const saveCostMutation = useSaveCostRowMutation();
    const saveAttorneyFeeSettingsMutation = useSaveAttorneyFeeSettings();
    const saveNegotiatedAmountsMutation = useSaveNegotiatedAmounts();

    // State for COST row (stored as string numbers, formatted by renderEditableCell)
    const [costData, setCostData] = useState<CostData>({
        bill: "0",
        insurance: "0",
        adj: "0",
        self: "0",
        medPay: "0",
        currentBalance: "0",
        reduction: "0",
        sent: "",
        approved: "",
    });

    // State for cell editing
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [editValue, setEditValue] = useState("");

    // State for delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<DisbursementRow | null>(null);

    // State for settlement statement modal
    const [settlementModalOpen, setSettlementModalOpen] = useState(false);

    // Local state for disbursement rows to allow adding insurance companies
    const [localRows, setLocalRows] = useState<DisbursementRow[]>([]);

    // Sync server data to local state
    useEffect(() => {
        if (data?.data) {
            setLocalRows(data.data);
        }
    }, [data?.data]);

    // Read negotiatedLiability from backend (auto-calculated from counter fields in saveNegotiationForCase)
    // Use manual override if set, otherwise use auto-calculated value
    const latestOffer = useMemo(() => {
        if (!caseData?.data) {
            return 0;
        }

        // If manual override exists, use it
        if (
            caseData.data.negotiatedLiabilityManualOverride !== null &&
            caseData.data.negotiatedLiabilityManualOverride !== undefined
        ) {
            return parseNumber(caseData.data.negotiatedLiabilityManualOverride);
        }

        // Otherwise use auto-calculated value from backend
        return parseNumber(caseData.data.negotiatedLiability ?? 0);
    }, [
        caseData?.data,
        caseData?.data?.negotiatedLiability,
        caseData?.data?.negotiatedLiabilityManualOverride,
    ]);

    // Calculate negotiatedUIUIM from case data
    // Use manual override if set, otherwise use negotiatedUIUIM field
    const negotiatedUIUIM = useMemo(() => {
        if (!caseData?.data) {
            return 0;
        }

        // If manual override exists, use it
        if (
            caseData.data.negotiatedUiuimManualOverride !== null &&
            caseData.data.negotiatedUiuimManualOverride !== undefined
        ) {
            return parseNumber(caseData.data.negotiatedUiuimManualOverride);
        }

        if (!caseData.data.negotiatedUIUIM) {
            return 0;
        }
        return parseNumber(caseData.data.negotiatedUIUIM);
    }, [
        caseData?.data,
        caseData?.data?.negotiatedUIUIM,
        caseData?.data?.negotiatedUiuimManualOverride,
    ]);

    // Calculate total negotiated (liability + UI/UIM)
    const totalNegotiated = useMemo(() => {
        return latestOffer + negotiatedUIUIM;
    }, [latestOffer, negotiatedUIUIM]);

    // Check if negotiated amounts are manually overridden
    const isNegotiatedLiabilityManual = useMemo(() => {
        return (
            caseData?.data?.negotiatedLiabilityManualOverride !== null &&
            caseData?.data?.negotiatedLiabilityManualOverride !== undefined
        );
    }, [caseData?.data?.negotiatedLiabilityManualOverride]);

    const isNegotiatedUiuimManual = useMemo(() => {
        return (
            caseData?.data?.negotiatedUiuimManualOverride !== null &&
            caseData?.data?.negotiatedUiuimManualOverride !== undefined
        );
    }, [caseData?.data?.negotiatedUiuimManualOverride]);

    // Load COST row data from case data
    useEffect(() => {
        if (caseData?.data) {
            // Parse to number, then convert to string for consistent storage
            const parseValue = (val: string | number | null | undefined): string => {
                if (val == null) {
                    return "0";
                }
                const num = typeof val === "string" ? Number.parseFloat(val) : val;
                return Number.isNaN(num) ? "0" : num.toString();
            };

            setCostData({
                bill: parseValue(caseData.data.costBill),
                insurance: parseValue(caseData.data.costInsurance),
                adj: parseValue(caseData.data.costAdj),
                self: parseValue(caseData.data.costSelf),
                medPay: parseValue(caseData.data.costMedPay),
                currentBalance: parseValue(caseData.data.costCurrentBalance),
                reduction: parseValue(caseData.data.costReduction),
                sent: caseData.data.costSent || "",
                approved: caseData.data.costApproved || "",
            });
        }
    }, [
        caseData?.data?.costBill,
        caseData?.data?.costInsurance,
        caseData?.data?.costAdj,
        caseData?.data?.costSelf,
        caseData?.data?.costMedPay,
        caseData?.data?.costCurrentBalance,
        caseData?.data?.costReduction,
        caseData?.data?.costSent,
        caseData?.data?.costApproved,
    ]);

    // Filter rows once and reuse to avoid redundant filtering
    const filteredRows = useMemo(() => {
        const provider = localRows.filter((r) => r.source === "provider");
        const loan = localRows.filter((r) => r.source === "loan");
        const insurance = localRows.filter((r) => r.source === "insurance");
        const providerLoan = [...provider, ...loan];

        return {
            provider,
            loan,
            insurance,
            providerLoan,
            providerReductions: providerLoan.reduce(
                (acc, row) => acc + parseNumber(row.reduction),
                0,
            ),
            insuranceReductions: insurance.reduce(
                (acc, row) => acc + parseNumber(row.reduction),
                0,
            ),
        };
    }, [localRows]);

    // Use centralized calculations hook
    const calculationsInput = useMemo(
        () => ({
            totalNegotiated: totalNegotiated,
            attorneyFeePercentage: caseData?.data?.attorneyFeePercentage ?? null,
            attorneyFeeManualOverride: caseData?.data?.attorneyFeeManualOverride ?? null,
            clientDistributionManualOverride:
                caseData?.data?.clientDistributionManualOverride ?? null,
            providerReductions: filteredRows.providerReductions,
            insuranceReductions: filteredRows.insuranceReductions,
            costReduction: parseNumber(costData.reduction),
        }),
        [totalNegotiated, caseData?.data, filteredRows, costData.reduction],
    );

    const calculations = useDisbursementsCalculations(calculationsInput);

    const handleEdit = (rowId: string, field: string, currentValue: string) => {
        setEditingCell({ rowId, field });
        setEditValue(currentValue);
    };

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.select();
    };

    const handleSave = async () => {
        if (editingCell) {
            if (editingCell.rowId === "cost") {
                // Parse numeric fields for API
                const parseNumericField = (value: string): number => {
                    return Number.parseFloat(value.replace(/[$,]/g, "")) || 0;
                };

                // Save to database
                try {
                    await saveCostMutation.mutateAsync({
                        caseUUID: caseIdUUID,
                        costBill: parseNumericField(
                            editingCell.field === "bill" ? editValue : costData.bill,
                        ),
                        costInsurance: parseNumericField(
                            editingCell.field === "insurance" ? editValue : costData.insurance,
                        ),
                        costAdj: parseNumericField(
                            editingCell.field === "adj" ? editValue : costData.adj,
                        ),
                        costSelf: parseNumericField(
                            editingCell.field === "self" ? editValue : costData.self,
                        ),
                        costMedPay: parseNumericField(
                            editingCell.field === "medPay" ? editValue : costData.medPay,
                        ),
                        costCurrentBalance: parseNumericField(
                            editingCell.field === "currentBalance"
                                ? editValue
                                : costData.currentBalance,
                        ),
                        costReduction: parseNumericField(
                            editingCell.field === "reduction" ? editValue : costData.reduction,
                        ),
                        costSent: editingCell.field === "sent" ? editValue : costData.sent,
                        costApproved:
                            editingCell.field === "approved" ? editValue : costData.approved,
                    });
                } catch (error) {
                    console.error("Failed to save COST row data:", error);
                }
            } else if (editingCell.rowId === "atty" && editingCell.field === "reduction") {
                // Manual override for attorney fee
                const value = parseNumber(editValue);
                try {
                    await saveAttorneyFeeSettingsMutation.mutateAsync({
                        caseUUID: caseIdUUID,
                        attorneyFeePercentage: caseData?.data?.attorneyFeePercentage
                            ? parseNumber(caseData.data.attorneyFeePercentage)
                            : null,
                        attorneyFeeManualOverride: value,
                        clientDistributionManualOverride:
                            caseData?.data?.clientDistributionManualOverride ?? null,
                    });
                } catch (error) {
                    console.error("Failed to save attorney fee override:", error);
                }
            } else if (editingCell.rowId === "client" && editingCell.field === "reduction") {
                // Manual override for client distribution
                const value = parseNumber(editValue);
                try {
                    await saveAttorneyFeeSettingsMutation.mutateAsync({
                        caseUUID: caseIdUUID,
                        attorneyFeePercentage: caseData?.data?.attorneyFeePercentage
                            ? parseNumber(caseData.data.attorneyFeePercentage)
                            : null,
                        attorneyFeeManualOverride:
                            caseData?.data?.attorneyFeeManualOverride ?? null,
                        clientDistributionManualOverride: value,
                    });
                } catch (error) {
                    console.error("Failed to save client distribution override:", error);
                }
            } else {
                // Find the row being edited
                const row = localRows.find((r) => r.idUUID === editingCell.rowId);
                if (row) {
                    // Update local state immediately
                    setLocalRows((prev) =>
                        prev.map((r) =>
                            r.idUUID === editingCell.rowId
                                ? { ...r, [editingCell.field]: editValue }
                                : r,
                        ),
                    );

                    // Save to server
                    const parseNumericField = (value: string | number): number => {
                        if (typeof value === "string") {
                            return Number.parseFloat(value.replace(/[$,]/g, "")) || 0;
                        }
                        return typeof value === "number" ? value : 0;
                    };

                    await saveMutation.mutateAsync({
                        caseUUID: caseIdUUID,
                        idUUID: row.idUUID,
                        source: row.source,
                        sourcePartyId: row.sourcePartyId || null,
                        company: editingCell.field === "company" ? editValue : row.company || "",
                        accountNumber:
                            editingCell.field === "accountNumber"
                                ? editValue
                                : row.accountNumber || null,
                        type: editingCell.field === "type" ? editValue : row.type || "",
                        bill:
                            editingCell.field === "bill"
                                ? parseNumericField(editValue)
                                : parseNumber(row.bill),
                        insurance:
                            editingCell.field === "insurance"
                                ? parseNumericField(editValue)
                                : parseNumber(row.insurance),
                        adj:
                            editingCell.field === "adj"
                                ? parseNumericField(editValue)
                                : parseNumber(row.adj),
                        self:
                            editingCell.field === "self"
                                ? parseNumericField(editValue)
                                : parseNumber(row.self),
                        medPay:
                            editingCell.field === "medPay"
                                ? parseNumericField(editValue)
                                : parseNumber(row.medPay),
                        currentBalance:
                            editingCell.field === "currentBalance"
                                ? parseNumericField(editValue)
                                : parseNumber(row.currentBalance),
                        reduction:
                            editingCell.field === "reduction"
                                ? parseNumericField(editValue)
                                : parseNumber(row.reduction),
                        sent: editingCell.field === "sent" ? editValue || null : row.sent || null,
                        approved:
                            editingCell.field === "approved"
                                ? editValue || null
                                : row.approved || null,
                        sortOrder: row.sortOrder || 0,
                    });
                }
            }
        }
        setEditingCell(null);
        setEditValue("");
    };

    const handleCancel = () => {
        setEditingCell(null);
        setEditValue("");
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSave();
        } else if (event.key === "Escape") {
            handleCancel();
        }
    };

    const isEditing = (rowId: string, field: string) => {
        return editingCell?.rowId === rowId && editingCell?.field === field;
    };

    const renderEditableCell = (
        rowId: string,
        field: keyof DisbursementRow | keyof CostData,
        value: string | number | null | undefined,
        isCompanyField = false,
    ) => {
        const currencyFields = [
            "bill",
            "insurance",
            "adj",
            "self",
            "medPay",
            "currentBalance",
            "reduction",
        ];
        const isCurrencyField = currencyFields.includes(field as string);
        const displayValue = isCurrencyField ? formatCurrency(value) : value || "";
        const isCurrentlyEditing = isEditing(rowId, field as string);

        if (isCurrentlyEditing) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                    <EditInput
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onFocus={handleInputFocus}
                        size="small"
                        autoFocus
                        multiline={isCompanyField}
                        rows={isCompanyField ? 2 : 1}
                        sx={{
                            width: isCompanyField ? "100%" : "80px",
                            minWidth: isCompanyField ? "auto" : "80px",
                        }}
                    />
                    <EditActions>
                        <IconButton size="small" onClick={handleSave} color="primary">
                            <Check size={14} />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancel} color="error">
                            <X size={14} />
                        </IconButton>
                    </EditActions>
                </Box>
            );
        }

        return (
            <CellContent>
                <span style={{ textAlign: isCompanyField ? "left" : "center", width: "100%" }}>
                    {displayValue}
                </span>
                <EditButton
                    className="edit-button"
                    size="small"
                    onClick={() => handleEdit(rowId, field as string, String(displayValue))}
                >
                    <Edit size={12} />
                </EditButton>
            </CellContent>
        );
    };

    const renderCompanyCell = (row: DisbursementRow) => {
        const isCurrentlyEditing = isEditing(row.idUUID, "company");

        if (isCurrentlyEditing) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <EditInput
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onFocus={handleInputFocus}
                        size="small"
                        fullWidth
                        autoFocus
                        multiline
                        rows={2}
                    />
                    <EditActions>
                        <IconButton size="small" onClick={handleSave} color="primary">
                            <Check size={14} />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancel} color="error">
                            <X size={14} />
                        </IconButton>
                    </EditActions>
                </Box>
            );
        }

        const getSourceChip = () => {
            if (row.source === "provider") {
                return <AutoPopulatedChip label="Medical Provider" size="small" />;
            }

            if (row.source === "loan") {
                return (
                    <Chip
                        label="Loan"
                        size="small"
                        sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            backgroundColor: "#2196f3",
                            color: "#ffffff",
                            fontWeight: 500,
                            marginTop: (theme) => theme.spacing(0.5),
                            alignSelf: "flex-start",
                            "& .MuiChip-label": { padding: (theme) => theme.spacing(0, 1) },
                        }}
                    />
                );
            }

            if (row.source === "insurance") {
                return (
                    <Chip label="Insurance" size="small" sx={{ fontSize: "0.65rem", height: 20 }} />
                );
            }
            return null;
        };

        return (
            <CellContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Box
                        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                    >
                        <CompanyName>{row.company || ""}</CompanyName>
                        {row.accountNumber && (
                            <AccountInfo>Account #: {row.accountNumber}</AccountInfo>
                        )}
                        {getSourceChip()}
                    </Box>
                </Box>
                <EditButton
                    className="edit-button"
                    size="small"
                    onClick={() => handleEdit(row.idUUID, "company", row.company || "")}
                >
                    <Edit size={12} />
                </EditButton>
            </CellContent>
        );
    };

    const handleAddInsuranceCompany = async () => {
        await saveMutation.mutateAsync({
            caseUUID: caseIdUUID,
            source: "insurance",
            sourcePartyId: null,
            company: "New Insurance Company",
            accountNumber: null,
            type: "Medical Provider",
            bill: 0,
            insurance: 0,
            adj: 0,
            self: 0,
            medPay: 0,
            currentBalance: 0,
            reduction: 0,
            sent: null,
            approved: null,
            sortOrder: localRows.length,
        });
    };

    const handleDeleteRow = (row: DisbursementRow) => {
        setRowToDelete(row);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (rowToDelete) {
            await deleteMutation.mutateAsync({
                caseUUID: caseIdUUID,
                rowId: rowToDelete.idUUID,
            });
        }
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setRowToDelete(null);
    };

    const handleDownloadReport = () => {
        setSettlementModalOpen(true);
    };

    // Calculate subtotals for provider/loan rows using pre-filtered arrays
    const subtotals = useMemo(() => {
        return filteredRows.providerLoan.reduce(
            (acc, row) => {
                return {
                    bill: acc.bill + parseNumber(row.bill),
                    insurance: acc.insurance + parseNumber(row.insurance),
                    adj: acc.adj + parseNumber(row.adj),
                    self: acc.self + parseNumber(row.self),
                    medPay: acc.medPay + parseNumber(row.medPay),
                };
            },
            { bill: 0, insurance: 0, adj: 0, self: 0, medPay: 0 },
        );
    }, [filteredRows.providerLoan]);

    // Use pre-filtered row arrays
    const providerRows = filteredRows.provider;
    const loanRows = filteredRows.loan;
    const insuranceRows = filteredRows.insurance;

    if (isLoading) {
        return <Box>Loading...</Box>;
    }

    if (isError) {
        return <Box>Error loading disbursements</Box>;
    }

    return (
        <Box sx={{ padding: 3 }}>
            {/* Header Section */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 3,
                }}
            >
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 500, color: "text.primary" }}>
                    Disbursements
                </Typography>
                <Button
                    startIcon={<Download size={16} />}
                    onClick={handleDownloadReport}
                    sx={{
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        textTransform: "none",
                        fontWeight: 500,
                        padding: "8px 24px",
                        "&:hover": {
                            backgroundColor: "success.dark",
                        },
                    }}
                >
                    Download Settlement Statement
                </Button>
            </Box>

            {/* Negotiated Amounts Section */}
            <Box
                sx={{
                    mb: 3,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    display: "grid",
                    gridTemplateColumns:
                        "minmax(200px, 250px) repeat(7, minmax(80px, 1fr)) minmax(100px, 1fr) repeat(3, minmax(80px, 1fr))",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box sx={{ gridColumn: "1 / 8", display: "flex", alignItems: "center", gap: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Negotiated Liability:
                        </Typography>
                        {editingCell?.rowId === "negotiated-liability" &&
                        editingCell?.field === "value" ? (
                            <>
                                <EditInput
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onFocus={handleInputFocus}
                                    onKeyDown={async (e) => {
                                        if (e.key === "Enter") {
                                            const value = parseNumber(editValue);
                                            await saveNegotiatedAmountsMutation.mutateAsync({
                                                caseUUID: caseIdUUID,
                                                negotiatedLiabilityManualOverride: value,
                                                negotiatedUiuimManualOverride:
                                                    caseData?.data?.negotiatedUiuimManualOverride ??
                                                    null,
                                            });
                                            setEditingCell(null);
                                        } else if (e.key === "Escape") {
                                            setEditingCell(null);
                                        }
                                    }}
                                    autoFocus
                                    size="small"
                                    sx={{ width: 120 }}
                                />
                                <EditActions>
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            const value = parseNumber(editValue);
                                            await saveNegotiatedAmountsMutation.mutateAsync({
                                                caseUUID: caseIdUUID,
                                                negotiatedLiabilityManualOverride: value,
                                                negotiatedUiuimManualOverride:
                                                    caseData?.data?.negotiatedUiuimManualOverride ??
                                                    null,
                                            });
                                            setEditingCell(null);
                                        }}
                                    >
                                        <Check size={16} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => setEditingCell(null)}>
                                        <X size={16} />
                                    </IconButton>
                                </EditActions>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                    {formatCurrency(latestOffer)}
                                </Typography>
                                <EditButton
                                    size="small"
                                    onClick={() =>
                                        handleEdit(
                                            "negotiated-liability",
                                            "value",
                                            latestOffer.toString(),
                                        )
                                    }
                                >
                                    <Edit size={14} />
                                </EditButton>
                            </>
                        )}
                        {isNegotiatedLiabilityManual && (
                            <AutoPopulatedChip
                                label="Manual Override"
                                size="small"
                                sx={{ backgroundColor: "#ff9800" }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            Negotiated UI/UIM:
                        </Typography>
                        {editingCell?.rowId === "negotiated-uiuim" &&
                        editingCell?.field === "value" ? (
                            <>
                                <EditInput
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onFocus={handleInputFocus}
                                    onKeyDown={async (e) => {
                                        if (e.key === "Enter") {
                                            const value = parseNumber(editValue);
                                            await saveNegotiatedAmountsMutation.mutateAsync({
                                                caseUUID: caseIdUUID,
                                                negotiatedLiabilityManualOverride:
                                                    caseData?.data
                                                        ?.negotiatedLiabilityManualOverride ?? null,
                                                negotiatedUiuimManualOverride: value,
                                            });
                                            setEditingCell(null);
                                        } else if (e.key === "Escape") {
                                            setEditingCell(null);
                                        }
                                    }}
                                    autoFocus
                                    size="small"
                                    sx={{ width: 120 }}
                                />
                                <EditActions>
                                    <IconButton
                                        size="small"
                                        onClick={async () => {
                                            const value = parseNumber(editValue);
                                            await saveNegotiatedAmountsMutation.mutateAsync({
                                                caseUUID: caseIdUUID,
                                                negotiatedLiabilityManualOverride:
                                                    caseData?.data
                                                        ?.negotiatedLiabilityManualOverride ?? null,
                                                negotiatedUiuimManualOverride: value,
                                            });
                                            setEditingCell(null);
                                        }}
                                    >
                                        <Check size={16} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => setEditingCell(null)}>
                                        <X size={16} />
                                    </IconButton>
                                </EditActions>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                    {formatCurrency(negotiatedUIUIM)}
                                </Typography>
                                <EditButton
                                    size="small"
                                    onClick={() =>
                                        handleEdit(
                                            "negotiated-uiuim",
                                            "value",
                                            negotiatedUIUIM.toString(),
                                        )
                                    }
                                >
                                    <Edit size={14} />
                                </EditButton>
                            </>
                        )}
                        {isNegotiatedUiuimManual && (
                            <AutoPopulatedChip
                                label="Manual Override"
                                size="small"
                                sx={{ backgroundColor: "#ff9800" }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                        pl: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        Total Negotiated:
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "primary.main", whiteSpace: "nowrap" }}
                    >
                        {formatCurrency(totalNegotiated)}
                    </Typography>
                </Box>
            </Box>

            <DisbursementsTable>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableHeadCell>Company</StyledTableHeadCell>
                            <StyledTableHeadCell>Type</StyledTableHeadCell>
                            <StyledTableHeadCell>Bill</StyledTableHeadCell>
                            <StyledTableHeadCell>Insurance</StyledTableHeadCell>
                            <StyledTableHeadCell>Adj</StyledTableHeadCell>
                            <StyledTableHeadCell>Self</StyledTableHeadCell>
                            <StyledTableHeadCell>MedPay</StyledTableHeadCell>
                            <StyledTableHeadCell>Current Balance</StyledTableHeadCell>
                            <StyledTableHeadCell>Reduction</StyledTableHeadCell>
                            <StyledTableHeadCell>Sent</StyledTableHeadCell>
                            <StyledTableHeadCell>Approved</StyledTableHeadCell>
                            <StyledTableHeadCell>Actions</StyledTableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Provider Rows */}
                        {providerRows.map((row) => (
                            <EditableRow key={row.idUUID} hover>
                                <CompanyCell>{renderCompanyCell(row)}</CompanyCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "type", row.type)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "bill", row.bill)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "insurance", row.insurance)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "adj", row.adj)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "self", row.self)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "medPay", row.medPay)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(
                                        row.idUUID,
                                        "currentBalance",
                                        row.currentBalance,
                                    )}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "reduction", row.reduction)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "sent", row.sent)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "approved", row.approved)}
                                </EditableCell>
                                <StyledTableCell align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteRow(row)}
                                        color="error"
                                    >
                                        <Trash2 size={16} />
                                    </IconButton>
                                </StyledTableCell>
                            </EditableRow>
                        ))}

                        {/* Subtotals Row */}
                        <TableRow
                            sx={{
                                backgroundColor: (theme) => theme.palette.background.default,
                                "& td": {
                                    fontWeight: 600,
                                    borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                                },
                            }}
                        >
                            <StyledTableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Provider Subtotals
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(subtotals.bill)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(subtotals.insurance)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(subtotals.adj)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(subtotals.self)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(subtotals.medPay)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell colSpan={5} />
                        </TableRow>

                        {/* Loan Rows */}
                        {loanRows.map((row) => (
                            <EditableRow key={row.idUUID} hover>
                                <CompanyCell>{renderCompanyCell(row)}</CompanyCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "type", row.type)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "bill", row.bill)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "insurance", row.insurance)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "adj", row.adj)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "self", row.self)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "medPay", row.medPay)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(
                                        row.idUUID,
                                        "currentBalance",
                                        row.currentBalance,
                                    )}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "reduction", row.reduction)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "sent", row.sent)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "approved", row.approved)}
                                </EditableCell>
                                <StyledTableCell align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteRow(row)}
                                        color="error"
                                    >
                                        <Trash2 size={16} />
                                    </IconButton>
                                </StyledTableCell>
                            </EditableRow>
                        ))}
                        {/* Insurance Company Rows */}
                        {insuranceRows.map((row) => (
                            <EditableRow key={row.idUUID} hover>
                                <CompanyCell>{renderCompanyCell(row)}</CompanyCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "type", row.type)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "bill", row.bill)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "insurance", row.insurance)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "adj", row.adj)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "self", row.self)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "medPay", row.medPay)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(
                                        row.idUUID,
                                        "currentBalance",
                                        row.currentBalance,
                                    )}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "reduction", row.reduction)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "sent", row.sent)}
                                </EditableCell>
                                <EditableCell>
                                    {renderEditableCell(row.idUUID, "approved", row.approved)}
                                </EditableCell>
                                <StyledTableCell align="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteRow(row)}
                                        color="error"
                                    >
                                        <Trash2 size={16} />
                                    </IconButton>
                                </StyledTableCell>
                            </EditableRow>
                        ))}

                        {/* COST Row */}
                        <SpecialRow rowType="cost">
                            <StyledTableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    COST
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell />
                            <EditableCell>
                                {renderEditableCell("cost", "bill", costData.bill)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "insurance", costData.insurance)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "adj", costData.adj)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "self", costData.self)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "medPay", costData.medPay)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell(
                                    "cost",
                                    "currentBalance",
                                    costData.currentBalance,
                                )}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "reduction", costData.reduction)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "sent", costData.sent)}
                            </EditableCell>
                            <EditableCell>
                                {renderEditableCell("cost", "approved", costData.approved)}
                            </EditableCell>
                            <StyledTableCell />
                        </SpecialRow>

                        {/* Atty Row */}
                        <SpecialRow rowType="atty">
                            <StyledTableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Attorney Fee
                                    </Typography>
                                    {calculations.isAttorneyFeeManual && (
                                        <AutoPopulatedChip
                                            label="Manual Override"
                                            size="small"
                                            sx={{ backgroundColor: "#ff9800" }}
                                        />
                                    )}
                                </Box>
                            </StyledTableCell>
                            <EditableCell>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        justifyContent: "center",
                                    }}
                                >
                                    <EditInput
                                        type="number"
                                        value={calculations.attorneyFeePercentage}
                                        onChange={async (e) => {
                                            const newPercentage =
                                                Number.parseFloat(e.target.value) || 33;
                                            await saveAttorneyFeeSettingsMutation.mutateAsync({
                                                caseUUID: caseIdUUID,
                                                attorneyFeePercentage: newPercentage,
                                                attorneyFeeManualOverride: null,
                                                clientDistributionManualOverride:
                                                    caseData?.data
                                                        ?.clientDistributionManualOverride ?? null,
                                            });
                                        }}
                                        size="small"
                                        sx={{ width: 80 }}
                                    />
                                    <Typography variant="body2">%</Typography>
                                </Box>
                            </EditableCell>
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(calculations.attorneyFeeAmount)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <EditableCell>
                                {renderEditableCell(
                                    "atty",
                                    "reduction",
                                    calculations.attorneyFeeAmount,
                                )}{" "}
                            </EditableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                        </SpecialRow>

                        {/* Client Row */}
                        <SpecialRow rowType="client">
                            <StyledTableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Client Distribution
                                    </Typography>
                                    {calculations.isClientDistributionManual && (
                                        <AutoPopulatedChip
                                            label="Manual Override"
                                            size="small"
                                            sx={{ backgroundColor: "#ff9800" }}
                                        />
                                    )}
                                </Box>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(calculations.clientDistributionAmount)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <EditableCell>
                                {renderEditableCell(
                                    "client",
                                    "reduction",
                                    calculations.clientDistributionAmount,
                                )}
                            </EditableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                        </SpecialRow>

                        {/* Total Row */}
                        <SpecialRow rowType="total">
                            <StyledTableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Settlement Total
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell />
                            <StyledTableCell align="center">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                                >
                                    {formatCurrency(totalNegotiated)}
                                </Typography>
                            </StyledTableCell>
                            <StyledTableCell colSpan={3} />
                        </SpecialRow>
                    </TableBody>
                </Table>
            </DisbursementsTable>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
                <Button
                    startIcon={<Plus size={14} />}
                    onClick={handleAddInsuranceCompany}
                    sx={{
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        padding: (theme) => theme.spacing(0.75, 2),
                        marginTop: (theme) => theme.spacing(2),
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                    }}
                >
                    Add Company / Loan
                </Button>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this disbursement row?
                        {rowToDelete?.company && (
                            <Box component="span" sx={{ display: "block", mt: 1, fontWeight: 600 }}>
                                {rowToDelete.company}
                            </Box>
                        )}
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Settlement Statement Modal */}
            <SettlementStatementModal
                open={settlementModalOpen}
                onClose={() => setSettlementModalOpen(false)}
                caseNumber={caseData?.data?.id?.toString() || ""}
                clientName={caseData?.data?.clientName || ""}
                caseIdUUID={caseIdUUID}
                dateOfLoss={
                    caseData?.data?.dateOfIncident ? caseData.data.dateOfIncident.toString() : ""
                }
                disbursementData={{
                    negotiatedLiability: latestOffer,
                    negotiatedUIUIM: negotiatedUIUIM,
                    totalNegotiated: totalNegotiated,
                    rows: localRows,
                    costData: costData,
                    attorneyFeePercentage: calculations.attorneyFeePercentage,
                    attorneyFeeAmount: calculations.attorneyFeeAmount,
                    clientDistributionAmount: calculations.clientDistributionAmount,
                }}
            />
        </Box>
    );
};

export default MrtDisbursementsTable;
